<template>
  <div>
    <v-row>
      <v-col md="2">
        <v-text-field
          hide-details="auto"
          type="date"
          solo
          flat
          background-color="grey lighten-4"
          v-model="searchParams.issued_at_start"
          label="Data Inicial"
        />
      </v-col>
      <v-col md="2">
        <v-text-field
          hide-details="auto"
          type="date"
          solo
          flat
          background-color="grey lighten-4"
          v-model="searchParams.issued_at_end"
          label="Data Final"
        />
      </v-col>
      <v-col md="3">
        <CompanySelect
          v-model="searchParams.company_origin_id"
          label="Origem"
          :item-value="(val) => val.id"
          :itemText="(val) => val.short_name"
          solo
          flat
          background-color="grey lighten-4"
          :dense="false"
          clearable
        />
      </v-col>
      <v-col md="3">
        <CompanySelect
          v-model="searchParams.company_destination_id"
          label="Destino"
          :item-value="(val) => val.id"
          :itemText="(val) => val.short_name"
          solo
          flat
          background-color="grey lighten-4"
          :dense="false"
          clearable
        />
      </v-col>
      <v-col md="2">
        <v-btn
          color="primary"
          block
          large
          @click="select(1)"
          :loading="isLoading"
        >
          <v-icon left>
            {{ hasUnsavedFilters ? "mdi-alert" : "mdi-magnify" }}
          </v-icon>
          Pesquisar
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col md="4">
        <ProductSearch
          v-model="searchParams.product_id"
          :item-value="(val) => val.id"
          solo
          flat
          background-color="grey lighten-4"
          clearable
        />
      </v-col>
      <v-col md="8"></v-col>
      <v-col md="4">
        <app-text-search-field
          label="Pesquisar"
          @input="handleSearchInput($event)"
          :loading="isLoading"
        />
      </v-col>
      <v-col md="6">
        <v-chip-group v-model="searchParams.status" label="Status" mandatory>
          <v-chip small filter value="draft">Rascunho</v-chip>
          <v-chip small filter value="confirmed">Confirmado</v-chip>
          <v-chip small filter value="received">Recebido</v-chip>
          <v-chip small filter value="canceled">Cancelado</v-chip>
          <v-chip small filter value="pending">Pendente</v-chip>
        </v-chip-group>
      </v-col>
    </v-row>

    <v-row class="mt-6">
      <v-col>
        <v-card flat color="grey lighten-4">
          <v-card-title class="d-flex justify-space-between mb-4 pt-5">
            <span class="text-subtitle-1">
              Produtos Transferidos Agrupados
            </span>
          </v-card-title>
          <v-card-text>
            <v-data-table
              class="grey lighten-4"
              :headers="headers"
              :items="transferredProducts.data"
              hide-default-footer
              :items-per-page="-1"
              disable-sort
            >
              <template v-slot:item.product="{ item }">
                <div class="d-flex align-center py-2">
                  <div>
                    <div class="font-weight-medium">
                      {{ item.product_code }} - {{ item.product_name }}
                    </div>
                  </div>
                </div>
              </template>

              <template v-slot:item.company_origin="{ item }">
                <div class="d-flex align-center">
                  <v-icon small left>mdi-store-marker</v-icon>
                  {{ item.company_origin }}
                </div>
              </template>

              <template v-slot:item.company_destination="{ item }">
                <div class="d-flex align-center">
                  <v-icon small left>mdi-store-check</v-icon>
                  {{ item.company_destination }}
                </div>
              </template>

              <template v-slot:item.total_quantity="{ item }">
                {{ $format.decimal(item.total_quantity) }}
              </template>

              <template v-slot:item.total_transfers="{ item }">
                {{ item.total_transfers }}
              </template>
            </v-data-table>

            <app-pagination
              @click="select($event)"
              :data="transferredProducts"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { format, startOfMonth } from "date-fns";
import CompanySelect from "@/components/company/ui/CompanySelect.vue";
import ProductSearch from "@/components/product/ui/ProductSearch.vue";
import DeliveryStatusChip from "@/components/delivery/section/DeliveryStatusChip.vue";

export default {
  components: {
    CompanySelect,
    DeliveryStatusChip,
    ProductSearch,
  },

  data() {
    return {
      headers: [
        { text: "Produto", value: "product", align: "start" },
        { text: "Origem", value: "company_origin", align: "start" },
        { text: "Destino", value: "company_destination", align: "start" },
        { text: "Quantidade Total", value: "total_quantity", align: "end" },
        { text: "Transferências", value: "total_transfers", align: "end" },
      ],

      transferredProducts: {
        data: [],
      },

      searchParams: {
        status: "pending",
        issued_at_start: format(startOfMonth(new Date()), "yyyy-MM-dd"),
        issued_at_end: format(new Date(), "yyyy-MM-dd"),
        company_origin_id: null,
        company_destination_id: null,
        product_id: null,
        text: null,
        page: 1,
      },

      lastSearchParams: null,
      isLoading: false,
    };
  },

  computed: {
    hasUnsavedFilters() {
      if (!this.lastSearchParams) return false;

      return (
        this.searchParams.status !== this.lastSearchParams.status ||
        this.searchParams.issued_at_start !==
          this.lastSearchParams.issued_at_start ||
        this.searchParams.issued_at_end !==
          this.lastSearchParams.issued_at_end ||
        this.searchParams.company_origin_id !==
          this.lastSearchParams.company_origin_id ||
        this.searchParams.company_destination_id !==
          this.lastSearchParams.company_destination_id ||
        this.searchParams.product_id !== this.lastSearchParams.product_id ||
        this.searchParams.text !== this.lastSearchParams.text
      );
    },
  },

  methods: {
    async select(page = 1) {
      this.searchParams.page = page;

      this.isLoading = true;

      try {
        const response = await this.$http.index(
          "stock/stock-transfer-report",
          this.searchParams
        );

        this.transferredProducts = response.transferredProducts;

        this.lastSearchParams = { ...this.searchParams };
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    handleSearchInput(search) {
      this.searchParams.text = search ? search : null;
    },
  },

  mounted() {
    this.select();
  },
};
</script>
